import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProjectById, getProjects, deleteProject } from '../api/projects';
import { fetchMilestones } from '../api/milestones';
import { getTasksByProject } from '../api/tasks';
import { getProgrammeById } from '../api/programmes';
import { Project } from '../types/project';
import { Milestone } from '../types/milestone';
import { Task } from '../types/task';
import ProjectForm from '../components/ProjectForm';
import DeleteProjectModal from '../components/DeleteProjectModal';
import Button from '../components/Button';
import Badge from '../components/Badge';
import EntityDetailLayout from '../components/detail/EntityDetailLayout';
import DetailChildList, { DetailRow } from '../components/detail/DetailChildList';

const formatDate = (dateString?: string): string =>
  dateString ? new Date(dateString).toLocaleDateString() : 'No due date';

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [programmeName, setProgrammeName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [projectData, milestonesData, tasksData, allProjectsData] = await Promise.all([
        getProjectById(id),
        fetchMilestones(id),
        getTasksByProject(id),
        getProjects(),
      ]);
      setProject(projectData);
      setMilestones(milestonesData);
      setTasks(tasksData);
      setAllProjects(allProjectsData);

      if (projectData.programme_id) {
        try {
          const programme = await getProgrammeById(projectData.programme_id);
          setProgrammeName(programme.name);
        } catch {
          setProgrammeName(null);
        }
      } else {
        setProgrammeName(null);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDeleteConfirm = async (cascade: boolean, reassignTo?: string) => {
    if (!id) return;
    try {
      await deleteProject(id, cascade, reassignTo);
      navigate(project?.programme_id ? `/programmes/${project.programme_id}` : '/projects');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
      setDeleting(false);
    }
  };

  // Metrics computed client-side from the project's tasks/milestones
  const completedTasks = tasks.filter((t) => t.status === 'completed').length;
  const completionRate = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;
  const now = new Date();
  const overdueTasks = tasks.filter(
    (t) => t.due_date && new Date(t.due_date) < now && t.status !== 'completed'
  ).length;

  const breadcrumb = [
    { label: 'Programmes', path: '/programmes' },
    ...(project?.programme_id
      ? [{ label: programmeName ?? 'Programme', path: `/programmes/${project.programme_id}` }]
      : []),
    { label: project?.name ?? 'Project' },
  ];

  const milestoneRows: DetailRow[] = milestones.map((milestone) => ({
    id: milestone.id,
    to: `/milestones/${milestone.id}`,
    title: milestone.name,
    subtitle: milestone.description ?? undefined,
    badges: milestone.status ? (
      <Badge status={milestone.status as any} size="sm">
        {milestone.status.replace('_', ' ')}
      </Badge>
    ) : undefined,
    meta: `${(milestone.completion_percentage ?? 0).toFixed(0)}%`,
  }));

  const taskRows: DetailRow[] = tasks.map((task) => ({
    id: task.id,
    to: `/tasks/${task.id}`,
    title: task.title,
    subtitle: task.description ?? undefined,
    badges: (
      <>
        <Badge priority={task.priority as any} size="sm">
          {task.priority}
        </Badge>
        <Badge status={task.status as any} size="sm">
          {task.status.replace('_', ' ')}
        </Badge>
      </>
    ),
    meta: task.due_date ? `Due ${formatDate(task.due_date)}` : undefined,
  }));

  return (
    <>
      <EntityDetailLayout
        breadcrumb={breadcrumb}
        loading={loading}
        error={error}
        title={project?.name ?? ''}
        subtitle={project?.description ?? undefined}
        headerBadges={
          project ? (
            <>
              <Badge status={(project.status || 'active') as any} size="md">
                {project.status || 'active'}
              </Badge>
              <Badge priority={(project.priority || 'low') as any} size="md">
                {project.priority || 'low'} priority
              </Badge>
            </>
          ) : undefined
        }
        metrics={
          project
            ? [
                { label: 'Total Tasks', value: tasks.length, accent: 'blue' },
                { label: 'Completed', value: completedTasks, accent: 'green' },
                { label: 'Overdue', value: overdueTasks, accent: 'amber' },
                { label: 'Milestones', value: milestones.length, accent: 'purple' },
              ]
            : undefined
        }
        progress={project ? { percentage: completionRate, label: 'Overall Progress' } : undefined}
        actions={
          project ? (
            <>
              <Button variant="primary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button variant="danger" size="sm" onClick={() => setDeleting(true)}>
                Delete
              </Button>
            </>
          ) : undefined
        }
      >
        <DetailChildList
          title="Milestones"
          rows={milestoneRows}
          emptyMessage="No milestones in this project yet."
        />
        <DetailChildList
          title="Tasks"
          rows={taskRows}
          emptyMessage="No tasks in this project yet."
        />
      </EntityDetailLayout>

      {editing && project && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-2xl my-8">
            <ProjectForm
              project={project}
              onSuccess={() => {
                setEditing(false);
                load();
              }}
              onCancel={() => setEditing(false)}
            />
          </div>
        </div>
      )}

      {deleting && project && (
        <DeleteProjectModal
          project={project}
          taskCount={tasks.length}
          availableProjects={allProjects}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleting(false)}
        />
      )}
    </>
  );
};

export default ProjectDetailPage;
